<template>
  <div :class="['container-single', domain.toLocaleLowerCase()]">
    <SingleContent v-if="isVisibleContent" :domain="domain" />
    <ChatFooter type-call="SINGLE" :domain="domain" :list-button-visible="footerOptions" />

    <teleport to="body">
      <SingleModalLanguage
        v-if="isVisibleModalLanguage && !isVisibleAlert"
        :mode="modalMode"
        :area="listTargetArea"
        @close="onCloseModalLanguage"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';
import { useRoute } from 'vue-router';

import { storeToRefs } from 'pinia';

import { fetchAuthCheck } from '@/api/apiService';
import SingleContent from '@/components/domains/chat/chat-single/SingleContent.vue';
import SingleModalLanguage from '@/components/domains/chat/chat-single/SingleModalLanguage.vue';
import ChatFooter from '@/components/domains/chat/common/ChatFooter.vue';
import { getLanguageByCode } from '@/static/languages';
import { useAlertStore } from '@/stores/alert';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { Language, TypeDomain } from '@/types/common';
import { ChatFooterOptions } from '@/types/components';
import { CHAT_FOOTER_PRESETS } from '@/utils/constants/chat';
import { DOMAIN } from '@/utils/constants/common';
import {
  ij_bottomBarStatus,
  ij_currentPopupMicInput,
  ij_isActiveFaceToFace,
  ij_isVisibleButtonMyLanguage,
  ij_isVisibleSecretController,
  ij_languageAway,
  ij_languageHome,
  ij_listTargetArea,
  ij_setBottomBarStatus,
  ij_setCurrentPopupMicInput,
  ij_setLanguageAway,
  ij_setLanguageHome,
  ij_setListTargetArea,
  ij_toggleIsActiveFaceToFace,
  ij_toggleIsVisibleSecretController,
} from '@/utils/injectionKeys';
import redirectToLoginPage from '@/utils/redirectToLoginPage';

const socketStore = useSocketStore();
const userStore = useUserStore();
const alertStore = useAlertStore();
const callStore = useCallStore();
const audioStore = useAudioStore();

const route = useRoute();

const { languageHome, languageAway } = storeToRefs(audioStore);
const { visible: isVisibleAlert } = storeToRefs(alertStore);

const isVisibleContent = ref<boolean>(false); // ApecSingleContent 가 보이는가
const isVisibleButtonMyLanguage = ref<boolean>(true); // 나의 언어만 보기 버튼 보임 여부
const isActiveFaceToFace = ref<boolean>(true); // 마주보기 모드 활성화 여부
const bottomBarStatus = ref<number>(1); // -2:recover / -1:failed / 0:on / 1:connecting / 2:end
const isVisibleSecretController = ref(false);
const isHanhwa: boolean = !!route.query.c;
const isCj: boolean = route.params.partnerCode === 'cj';

const currentPopupMicInput = ref<string>(''); // 현재 팝업
const listTargetArea = ref<string[]>([]); // 선택할 언어 목록

const isVisibleModalLanguage = computed(() => listTargetArea.value.length > 0);

const domain = computed<TypeDomain>(() => {
  const metaDomain = route.meta.domain;
  return metaDomain && Object.values(DOMAIN).includes(metaDomain) ? metaDomain : 'CHAT';
});

// layout에 따른 모달 방식 변경
const modalMode = computed<'small' | 'fill'>(() => {
  return domain.value === 'CHAT' ? 'fill' : 'small';
});

const footerOptions = computed<ChatFooterOptions[]>(() => {
  return [...(domain.value === 'CHAT' ? ['LOGO'] : []), ...CHAT_FOOTER_PRESETS.SINGLE];
});

// 큰 글씨로 보기 토글
const toggleIsActiveFaceToFace = () => {
  isActiveFaceToFace.value = !isActiveFaceToFace.value;
};

const toggleIsVisibleSecretController = () => {
  isVisibleSecretController.value = !isVisibleSecretController.value;
};

const setBottomBarStatus = (newVal: number) => {
  bottomBarStatus.value = newVal;
};

// 나의 언어 설정
const setLanguageHome = (language: Language) => {
  // 같은 언어가 될 수 없도록
  if (languageHome.value && language.code === languageAway.value?.code) {
    languageAway.value = languageHome.value;
  }

  languageHome.value = language;
  localStorage.setItem('language', language.code);

  if (!languageAway.value || socketStore.socketState !== 'OPEN') {
    return;
  }

  socketStore.sendMessageChangeLanguage(languageHome.value.code, languageAway.value.code);
  userStore.fetchUserInfo();
};

// 상대 언어 설정
const setLanguageAway = (language: Language) => {
  // 같은 언어가 될 수 없도록
  if (languageAway.value && language.code === languageHome.value.code) languageHome.value = languageAway.value;

  languageAway.value = language;
  if (!languageHome.value || socketStore.socketState !== 'OPEN') return;
  socketStore.sendMessageChangeLanguage(languageHome.value.code, languageAway.value.code);
};

// 팝업 설정
const setCurrentPopupMicInput = (popupName) => {
  currentPopupMicInput.value = popupName !== currentPopupMicInput.value ? popupName : '';
};

// 선택할 언어 설정
const setListTargetArea = (listArea?: string[]) => {
  listTargetArea.value = listArea ?? [];
};

// 언어 선택한 적 있는지 확인. 있으면 상대 언어만 노출
const handleSetupLanguageSelect = () => {
  const languageDefault = localStorage.getItem('language');

  if (languageDefault) {
    setLanguageHome(getLanguageByCode(languageDefault));
    setListTargetArea(['away']);
  } else {
    setListTargetArea(['home', 'away']);
  }
};

// 모달 닫힘 처리
const onCloseModalLanguage = (listArea?: string[]) => {
  setListTargetArea(listArea);
  isVisibleContent.value = true;
};

// 파트너코드 저장
const savePartnerCode = () => {
  const partnerCode = location.pathname.split('/').filter(Boolean)[2] ?? route.query.partnerCode;
  if (partnerCode) localStorage.setItem('partner-code-admin', partnerCode);
};

onMounted(async () => {
  if (domain.value === 'CHAT') {
    savePartnerCode();
  }

  callStore.init(); // qrCodeInfo 초기화 (다른 모드에서 사용한 options 가 유지되는 이슈가 있음)

  if (isHanhwa || isCj) userStore.fetchUserInfo();
  else {
    const { errorCode } = await fetchAuthCheck(userStore.userInfo?.loginId);
    if (errorCode === 4010) {
      userStore.requestLogout();
      redirectToLoginPage();
    }
  }

  const levelSize = userStore.isTablet ? 3 : 2;
  userStore.setTextMultiplier(levelSize);

  socketStore.connectionResponse = -1;

  if (domain.value === 'CHAT') {
    handleSetupLanguageSelect();
  } else if (domain.value === 'SELF_SERVICE') {
    onCloseModalLanguage();
  }

  await audioStore.fetchLanguageOptions();
});

onUnmounted(() => {
  userStore.setTextMultiplier(0);
});

provide(ij_isVisibleButtonMyLanguage, isVisibleButtonMyLanguage);
provide(ij_isActiveFaceToFace, isActiveFaceToFace);
provide(ij_toggleIsActiveFaceToFace, toggleIsActiveFaceToFace);
provide(ij_bottomBarStatus, bottomBarStatus);
provide(ij_setBottomBarStatus, setBottomBarStatus);
provide(ij_languageHome, languageHome);
provide(ij_languageAway, languageAway);
provide(ij_setLanguageHome, setLanguageHome);
provide(ij_setLanguageAway, setLanguageAway);
provide(ij_currentPopupMicInput, currentPopupMicInput);
provide(ij_setCurrentPopupMicInput, setCurrentPopupMicInput);
provide(ij_listTargetArea, listTargetArea);
provide(ij_setListTargetArea, setListTargetArea);
provide(ij_isVisibleSecretController, isVisibleSecretController);
provide(ij_toggleIsVisibleSecretController, toggleIsVisibleSecretController);
</script>

<style lang="scss" scoped>
.container-single {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  overflow: hidden;
  background: var(--background-theme);
}
</style>
